// components/TransactionActionsMenu.jsx
import React, { useState } from "react";
import {
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Snackbar,
  Alert,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { gql, useMutation } from "@apollo/client";

const APPROVE_REJECT_TRANSACTION = gql`
  mutation ApproveReject($record: AccountTransactionsInput!) {
    approveRejectAccountTransaction(record: $record) {
      data
      errorMessage
      status
    }
  }
`;

// Same helper idea as before, but also works when result is "Failed"
const getApolloErrorMessage = (err) => {
  const gqlErr = err?.graphQLErrors?.[0];
  if (gqlErr) {
    const extMsg = gqlErr?.extensions?.message;
    if (typeof extMsg === "string" && extMsg.trim()) return extMsg;
    if (typeof gqlErr.message === "string" && gqlErr.message.trim()) return gqlErr.message;
  }

  const netErrMsg =
    err?.networkError?.result?.errors?.[0]?.extensions?.message ||
    err?.networkError?.result?.errors?.[0]?.message;

  if (typeof netErrMsg === "string" && netErrMsg.trim()) return netErrMsg;

  if (typeof err?.message === "string" && err.message.trim()) return err.message;

  return "Something went wrong.";
};

const TransactionActionsMenu = ({
  transaction,
  onActionComplete,
  onViewBreakdown,
  showViewButton = false,
  hideViewInMenu = false,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [reasonForRejection, setReasonForRejection] = useState("");

  const [toast, setToast] = useState({ open: false, type: "success", msg: "" });
  const showToast = (type, msg) => setToast({ open: true, type, msg });

  const [approveReject, { loading }] = useMutation(APPROVE_REJECT_TRANSACTION);

  const handleOpen = (e) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleApprove = async () => {
    handleClose();
    await approveRejectAction(true);
  };

  const handleReject = () => {
    handleClose();
    setRejectDialogOpen(true);
  };

  const handleViewBreakdown = () => {
    handleClose();
    onViewBreakdown?.(transaction);
  };

  const approveRejectAction = async (approved) => {
    try {
      const variables = {
        record: {
          id: Number(transaction.id),
          approved,
          ...(approved ? {} : { reasonForRejection: reasonForRejection.trim() }),
        },
      };

      const { data } = await approveReject({ variables });

      const result = data?.approveRejectAccountTransaction;

      // If BE returned nothing, treat as error
      if (!result) {
        showToast("error", "No response received from server.");
        return;
      }

      // Your API uses string status: "Success" / "Failed"
      const ok = String(result.status).toLowerCase() === "success";

      if (ok) {
        showToast("success", approved ? "Approved successfully." : "Rejected successfully.");
        setReasonForRejection("");
        onActionComplete?.(); // let parent refetch
      } else {
        // This is your case: "Failed" + errorMessage
        showToast("error", result.errorMessage || "Operation failed.");
      }
    } catch (err) {
      console.error(err);
      showToast("error", getApolloErrorMessage(err));
    }
  };

  return (
    <>
      <Stack direction="row" alignItems="center" spacing={0.5}>
        {showViewButton && onViewBreakdown ? (
          <Button
            size="small"
            variant="outlined"
            color="secondary"
            startIcon={<VisibilityIcon fontSize="small" />}
            onClick={handleViewBreakdown}
            disabled={loading}
            sx={{ fontWeight: 700, textTransform: "none", whiteSpace: "nowrap" }}
          >
            View Breakdown
          </Button>
        ) : null}

        <IconButton onClick={handleOpen} disabled={loading} size="small">
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </Stack>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        {onViewBreakdown && !hideViewInMenu ? (
          <MenuItem onClick={handleViewBreakdown} disabled={loading}>
            <ListItemIcon>
              <VisibilityIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>View Breakdown</ListItemText>
          </MenuItem>
        ) : null}

        <MenuItem onClick={handleApprove} disabled={loading}>
          <ListItemIcon>
            <CheckIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Approve</ListItemText>
        </MenuItem>

        <MenuItem onClick={handleReject} disabled={loading}>
          <ListItemIcon>
            <CloseIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Reject</ListItemText>
        </MenuItem>
      </Menu>

      {/* Reject dialog */}
      <Dialog
        open={rejectDialogOpen}
        onClose={() => setRejectDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Reject Transaction</DialogTitle>
        <DialogContent>
          <TextField
            label="Reason for Rejection"
            fullWidth
            multiline
            rows={3}
            value={reasonForRejection}
            onChange={(e) => setReasonForRejection(e.target.value)}
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogOpen(false)} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={async () => {
              setRejectDialogOpen(false);
              await approveRejectAction(false);
            }}
            disabled={loading || !reasonForRejection.trim()}
          >
            Reject
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar toast (portal style) */}
      <Snackbar
        open={toast.open}
        autoHideDuration={4500}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setToast((t) => ({ ...t, open: false }))}
          severity={toast.type}
          sx={{ width: "100%" }}
        >
          {toast.msg}
        </Alert>
      </Snackbar>
    </>
  );
};

export default TransactionActionsMenu;
